import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  FileCategoryEnum,
  FileFormatEnum,
  LessonSkillCategoriesEnum,
  SportsEnum,
} from 'types/generated/client';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useUploadVideoFile } from 'hooks/useUploadVideoFile';
import { useViewer } from 'hooks/useViewer';
import Button from 'components/Button';
import FileUploader from 'components/VideoAnalysis/FileUploader';
import RecordAudio from 'components/VideoAnalysis/RecordAudio';
import RecordVideo from 'components/VideoAnalysis/RecordVideo';
import Skills from 'components/VideoAnalysis/Skills';
import classNames from 'styles/utils/classNames';
import { Step1Props } from '../../props';

const Step1: React.FC<Step1Props> = ({
  visible,
  control,
  getValues,
  setValue,
  setError,
  clearErrors,
  onChangeStep,
  submitting,
  trigger,
}) => {
  const { uploadVideoFile, uploadProgress } = useUploadVideoFile();

  const uploading = uploadProgress !== null && uploadProgress < 100;

  const viewer = useViewer();

  const currentUser = useGetCurrentUser();

  const handleMediaUpload = async (
    files: File[],
    onChange: any,
    fieldName: 'lessonReflectionFiles',
  ) => {
    try {
      const file = files[0];

      const fileUploadRes = await uploadVideoFile({ file });

      if (!fileUploadRes) {
        throw new Error('Video upload failed.');
      }

      const fileFormat = fileUploadRes.fileType.startsWith('video/')
        ? FileFormatEnum.Video
        : fileUploadRes.fileType.startsWith('audio/')
        ? FileFormatEnum.Audio
        : FileFormatEnum.Image;

      const formattedFile = {
        fileCategory: FileCategoryEnum.Other,
        fileName: fileUploadRes.fileName,
        fileType: fileUploadRes.fileType,
        host: fileUploadRes.host,
        originalFileName: fileUploadRes.originalFileName,
        provider: fileUploadRes.provider,
        providerUrl: fileUploadRes.providerUrl,
        fileFormat: fileFormat,
        url: fileUploadRes.url,
        providerId: '',
        userId: viewer.userId,
      };

      const currentFiles = getValues(fieldName);
      const existingFiles = Array.isArray(currentFiles)
        ? currentFiles
        : currentFiles
        ? [currentFiles]
        : [];
      const updatedFiles = [...existingFiles, formattedFile];

      onChange(updatedFiles);
    } catch (error) {
      console.error('Media upload failed:', error);
      setError(fieldName, {
        type: 'manual',
        message: 'Error uploading media. Please try again.',
      });
    }
  };

  const handleVoiceRecording = async (
    blob: Blob,
    fileType: string,
    onChange: any,
    fieldName: 'lessonReflectionFiles',
  ) => {
    try {
      clearErrors(fieldName);
      const file = new File([blob], `voice_recording.${fileType}`, { type: blob.type });
      handleMediaUpload([file], onChange, fieldName);
    } catch (error) {
      console.error('Voice recording upload failed:', error);
      setError(fieldName, {
        type: 'manual',
        message: 'Error uploading voice recording. Please try again.',
      });
    }
  };

  const handleVideoRecording = async (
    blob: Blob,
    fileType: string,
    onChange: any,
    fieldName: 'lessonReflectionFiles',
  ) => {
    try {
      clearErrors(fieldName);
      const file = new File([blob], `video_recording.${fileType}`, { type: blob.type });
      return handleMediaUpload([file], onChange, fieldName);
    } catch (error) {
      console.error('Video recording upload failed:', error);
      setError(fieldName, {
        type: 'manual',
        message: 'Error uploading video recording. Please try again.',
      });
    }
  };

  useEffect(() => {
    uploading ? toast.loading('Uploading media, please wait') : toast.dismiss();
  }, [uploading]);

  return (
    <div className={classNames('hidden', visible && '!block')}>
      <div className={'px-8'}>
        <div className="flex flex-col">
          <span className="typography-product-subheading">Key takeaway</span>
          <span className="typography-product-caption mt-1 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            What is the most important point you want the player to take away from this lesson?
          </span>
          <div className="mb-2 mt-4">
            <Controller
              name={`details`}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <textarea
                    {...field}
                    className="h-[80px] w-full rounded-md border-0 bg-brand-gray-50 pl-3 pr-3 font-light text-color-text-lightmode-primary focus:outline-0 dark:text-color-text-darkmode-primary"
                    placeholder="Type here"
                  />
                  {error && <p className="text-xs text-red-600">{error.message}</p>}
                </>
              )}
            />
          </div>

          <div className="mt-4">
            <span className="typography-product-subheading mb-2 block ">
              Attach or record media
            </span>

            <Controller
              name={`lessonReflectionFiles`}
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <div>
                  <div className="mb-4">
                    <FileUploader
                      uploadProgress={uploadProgress}
                      multiple={false}
                      accept={{
                        'image/*': [],
                        'video/*': ['.mp4', '.mov'],
                        'audio/*': ['.mp3', '.mp4', '.wav', '.m4a', '.ogg', '.webm'],
                      }}
                      styles="rounded-md"
                      selectedFiles={Array.isArray(value) ? value : value ? [value] : []}
                      onRemoveFile={(fileToRemove) => {
                        if (Array.isArray(value)) {
                          const updatedFiles = value.filter(
                            (file) => file.url !== fileToRemove.url,
                          );
                          onChange(updatedFiles.length > 0 ? updatedFiles : []);
                        } else {
                          onChange([]);
                        }
                      }}
                      onSelectFiles={(files) =>
                        handleMediaUpload(files, onChange, `lessonReflectionFiles`)
                      }
                      helperText="PNG, JPG, or MP4 (max. 20 MB image or 60 minute video)"
                    />
                  </div>

                  <div className="mt-1 flex gap-2">
                    <RecordAudio
                      centered
                      showPreview={false}
                      isUploading={uploading}
                      onRecordingComplete={(blob: Blob, fileType: string) => {
                        handleVoiceRecording(blob, fileType, onChange, `lessonReflectionFiles`);
                      }}
                    />
                    <RecordVideo
                      centered
                      showPreview={false}
                      onRecordingComplete={(blob: Blob, fileType: string) => {
                        handleVideoRecording(blob, fileType, onChange, 'lessonReflectionFiles');
                      }}
                      isUploading={uploading}
                    />
                  </div>
                  <div className="mt-2">
                    {error && <p className="text-xs text-red-600">{error.message}</p>}
                  </div>
                </div>
              )}
            />
          </div>
        </div>

        <div className="mt-6">
          <Skills
            control={control}
            label={true}
            allowCustom={true}
            getValues={getValues}
            setValue={setValue}
            fieldName="currentSkills"
            limit={3}
            labelText={'Skills focused on during the lesson'}
            intialSkillBucket={getValues('completedCategoryOrSport')}
            labelClassName={
              'text-color-text-lightmode-primary! dark:text-color-text-darkmode-primary!'
            }
            onCategoryOrSportChange={(val: LessonSkillCategoriesEnum | SportsEnum | null) => {
              if (!val) return;
              setValue('completedCategoryOrSport', val as LessonSkillCategoriesEnum | SportsEnum);
            }}
          />
        </div>

        <div className="mt-6">
          <Skills
            control={control}
            label={true}
            allowCustom={true}
            getValues={getValues}
            fieldName="futureSkills"
            setValue={setValue}
            limit={3}
            intialSkillBucket={getValues('upcomingCategoryOrSport')}
            onCategoryOrSportChange={(val: LessonSkillCategoriesEnum | SportsEnum | null) => {
              if (!val) return;
              setValue('upcomingCategoryOrSport', val as LessonSkillCategoriesEnum | SportsEnum);
            }}
            labelText={'Skills we will work on next lesson'}
            labelClassName={
              'text-color-text-lightmode-primary! dark:text-color-text-darkmode-primary!'
            }
          />
        </div>

        <div className="mt-6">
          <Skills
            control={control}
            label={true}
            allowCustom={true}
            getValues={getValues}
            fieldName="assignmentSkills"
            setValue={setValue}
            limit={3}
            isAssignment
            collapsible
            initialCollapsed={currentUser.user?.isPicklrP4Member === false} // open for p4 coaches
            intialSkillBucket={getValues('assignmentCategoryOrSport')}
            onCategoryOrSportChange={(val: LessonSkillCategoriesEnum | SportsEnum | null) => {
              if (!val) return;
              setValue('assignmentCategoryOrSport', val as LessonSkillCategoriesEnum | SportsEnum);
            }}
            labelText={'Skills to practice before the next lesson'}
            labelClassName={
              'text-color-text-lightmode-primary! dark:text-color-text-darkmode-primary!'
            }
          />
        </div>
      </div>
      <div className="mt-12 flex justify-end gap-4 border-t px-8 py-4">
        <Button
          variant="primary"
          size="lg"
          className="w-fit"
          onClick={async () => {
            const validated = await trigger();
            validated && onChangeStep();
          }}
          loading={submitting}
          disabled={uploading || submitting}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Step1;
